import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import React, { useRef, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { selectIcon } from 'assets/icons';

interface Props {
  id: string;
  name: string;
  label: string | null;
  value: string;
  onSelect: (value: string) => void;
  onBlur: (value: string) => void;
  isError?: boolean;
  errorText?: string;
  onCountrySelect: (country: string) => void;
}

const PlacesAutocomplete: React.FC<Props> = ({
  id,
  name,
  label,
  value,
  onSelect,
  isError,
  errorText,
  onCountrySelect,
  onBlur
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const {
    value: searchValue,
    suggestions: { data },
    setValue,
    clearSuggestions
  } = usePlacesAutocomplete({
    requestOptions: {
      language: 'en-GB',
    },
    debounce: 300,
    defaultValue: value, 
  });

  useOnclickOutside(() => {
    setIsOpen(false);
  }, { refs: [ref] });

  const handleSelect = async (address: string) => {
    try {
      const results = await getGeocode({ address });
      if (results[0]) {
        if (results[0].place_id && onCountrySelect) {
          onCountrySelect(results[0].place_id);
        }
      }
      
      onSelect(address);
      setValue(address, false);
      clearSuggestions();
      setIsOpen(false);
    } catch (error) {
      console.error('Error: ', error);
    }
  };

  return (
    <div>
      <div
        ref={ref}
        tabIndex={1}
        className={`select-container ${isError ? 'error' : ''}`}
      >
        <select style={{ display: 'none' }} />
        <span className={`input-label ${isError ? 'error' : ''}`}>{label}</span>
        
        <div className="select-options-container">
          <input
            type="text"
            className="place-select-options-text"
            value={searchValue}
            onChange={(e) => {
              setValue(e.target.value);
              setIsOpen(true);
            }}
            placeholder="Search address..."
            onBlur={() => onBlur(searchValue)}
          />
        </div>

        {isOpen && data.length > 0 && (
          <div className="select-options-item-container">
            {data.map((suggestion) => (
              <div
                key={suggestion.place_id}
                className="select-options-item"
                onClick={() => handleSelect(suggestion.description)}
              >
                {suggestion.description}
              </div>
            ))}
          </div>
        )}
      </div>
      {isError && <span className="input-label-error">{errorText}</span>}
    </div>
  );
};

export default PlacesAutocomplete;